<template>
  <v-card flat>
    <v-card-title class="grey lighten-4 px-2 py-0">
      <!-- Create Button -->
      <v-layout row wrap>
        <v-flex xs12 class="text-lg-right">
          <v-tooltip bottom>
            <v-btn color="primary" slot="activator" flat @click="createProcessingRecord()">
              <v-icon left>add_circle</v-icon>
              <span>Create</span>
            </v-btn>
            <span>Create new processing record</span>
          </v-tooltip>
        </v-flex>
      </v-layout>
    </v-card-title>
    <!-- Search Filters -->
    <v-card-text class="pb-0">
      <v-container grid-list-md fluid class="ma-0 pa-0">
        <v-layout row wrap>
          <!-- Title -->
          <v-flex md3 sm3>
            <v-text-field :label="messages.label.TITLE" v-model="title">
            </v-text-field>
          </v-flex>
          <!-- Provider Name -->
          <v-flex md3 sm3>
            <v-combobox v-model="selectedProviders" :label="messages.label.PROVIDER_NAME" multiple tags
              :loading="loadingProviders" item-text="title" item-value="title" :items="providers">
            </v-combobox>
          </v-flex>
          <!-- Processing Record State -->
          <v-flex md2 sm2>
            <v-select v-model="selectedStates" :label="messages.label.STATE" multiple :items="statesList"></v-select>
          </v-flex>
          <!-- LWS -->
          <v-flex md2 sm3>
            <v-menu ref="lwsDateMenu" lazy :close-on-content-click="false" v-model="lwsDateMenu"
              transition="scale-transition" offset-y full-width :nudge-right="40" :return-value.sync="lwsDate"
              max-width="290px" min-width="290px">
              <v-text-field slot="activator" :label="messages.label.LWS_DATE" v-model="lwsDate" clearable
                prepend-icon="event" readonly>
              </v-text-field>
              <v-date-picker v-model="lwsDate" :max="lwMaxStartDate" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="lwsDateMenu = false">{{
                    messages.label.CANCEL
                }}</v-btn>
                <v-btn flat color="primary" @click="$refs.lwsDateMenu.save(lwsDate)">{{ messages.label.OK }}</v-btn>
              </v-date-picker>
            </v-menu>
          </v-flex>
          <!-- LWE -->
          <v-flex md2 sm3>
            <v-menu ref="lweDateMenu" lazy :close-on-content-click="false" v-model="lweDateMenu"
              transition="scale-transition" offset-y full-width :nudge-right="40" :return-value.sync="lweDate"
              max-width="290px" min-width="290px">
              <v-text-field slot="activator" :label="messages.label.LWE_DATE" v-model="lweDate" prepend-icon="event"
                clearable readonly></v-text-field>
              <v-date-picker v-model="lweDate" :min="lwMinEndDate" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="lweDateMenu = false">{{
                    messages.label.CANCEL
                }}</v-btn>
                <v-btn flat color="primary" @click="$refs.lweDateMenu.save(lweDate)">{{ messages.label.OK }}</v-btn>
              </v-date-picker>
            </v-menu>
          </v-flex>
        </v-layout>
        <!-- Dynamic filters and search button -->
        <v-layout row wrap justify-start>
          <!-- Package ID -->
          <v-flex md3 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[0])">
            <v-text-field :label="messages.label.PACKAGE_ID" v-model="packageId" append-icon="remove_circle"
              @click:append="updateSelection(additionalSearchFields[0])">
            </v-text-field>
          </v-flex>
          <!-- Manifest ID -->
          <v-flex md3 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[1])">
            <v-text-field :label="messages.label.MANIFEST_ID" v-model="manifestId" append-icon="remove_circle"
              @click:append="updateSelection(additionalSearchFields[1])">
            </v-text-field>
          </v-flex>
          <!-- Added Start Date -->
          <v-flex md2 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[2])">
            <v-menu ref="startDateMenu" lazy :close-on-content-click="false" v-model="startDateMenu"
              transition="scale-transition" offset-y full-width :nudge-right="40" :return-value.sync="startDate"
              max-width="290px" min-width="290px">
              <v-text-field slot="activator" :label="messages.label.START_DATE" v-model="startDate" clearable
                prepend-icon="event" readonly append-icon="remove_circle"
                @click:append="updateSelection(additionalSearchFields[2])">
              </v-text-field>
              <v-date-picker v-model="startDate" :max="maxStartDate" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="startDateMenu = false">{{
                    messages.label.CANCEL
                }}</v-btn>
                <v-btn flat color="primary" @click="$refs.startDateMenu.save(startDate)">{{ messages.label.OK }}</v-btn>
              </v-date-picker>
            </v-menu>
          </v-flex>
          <!-- Added End Date -->
          <v-flex md2 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[2])">
            <v-menu ref="endDateMenu" lazy :close-on-content-click="false" v-model="endDateMenu"
              transition="scale-transition" offset-y full-width :nudge-right="40" :return-value.sync="endDate"
              max-width="290px" min-width="290px">
              <v-text-field slot="activator" :label="messages.label.END_DATE" v-model="endDate" prepend-icon="event"
                clearable readonly append-icon="remove_circle"
                @click:append="updateSelection(additionalSearchFields[2])"></v-text-field>
              <v-date-picker v-model="endDate" :min="minEndDate" no-title scrollable>
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="endDateMenu = false">{{
                    messages.label.CANCEL
                }}</v-btn>
                <v-btn flat color="primary" @click="$refs.endDateMenu.save(endDate)">{{ messages.label.OK }}</v-btn>
              </v-date-picker>
            </v-menu>
          </v-flex>
          <!-- PCT -->
          <v-flex md3 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[3])">
            <v-text-field :label="messages.label.PCT" v-model="pct" append-icon="remove_circle"
              @click:append="updateSelection(additionalSearchFields[3])"></v-text-field>
          </v-flex>
          <!-- Origination ID -->
          <v-flex md3 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[4])">
            <v-text-field :label="messages.label.ORIGINATION_ID" v-model="originationId" append-icon="remove_circle"
              @click:append="updateSelection(additionalSearchFields[4])"></v-text-field>
          </v-flex>
          <!-- Package Asset Name -->
          <v-flex md3 sm3 v-if="isAdditionalSearchFieldVisible(additionalSearchFields[5])">
            <v-text-field :label="messages.label.PACKAGE_ASSET_NAME" v-model="packageAssetName"
              append-icon="remove_circle" @click:append="updateSelection(additionalSearchFields[5])"></v-text-field>
          </v-flex>
          <!-- Add More Filters button -->
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-tooltip bottom>
                <v-btn v-on="on" color="primary" slot="activator" flat>
                  <v-icon left>add_circle</v-icon>
                  <span>More</span>
                </v-btn>
                <span>Add more search fields</span>
              </v-tooltip>
            </template>
            <v-list>
              <v-list-tile v-for="(item, index) in additionalSearchFields" :key="index" ripple
                @click="updateSelection(item)">
                <v-list-tile-content>
                  <v-list-tile-title>{{ item.searchField }}</v-list-tile-title>
                </v-list-tile-content>
                <v-list-tile-action>
                  <v-icon v-if="item.visible" color="red lighten-1">
                    remove_circle
                  </v-icon>
                  <v-icon v-else color="green darken-2">
                    add_circle
                  </v-icon>
                </v-list-tile-action>
              </v-list-tile>
            </v-list>
          </v-menu>
          <!-- Search Button -->
          <v-flex md1 sm1>
            <v-btn color="primary" @click="searchProcessingRecords">{{
                messages.label.SEARCH
            }}</v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
    <CreateProcessingRecord />
  </v-card>
</template>

<script>
import moment from "moment"
import { mapState, mapActions } from "vuex"
import PreIngestService from "@/PreIngest/PreIngestService"
import AppService from "@/AppService"
import { GET_PROVIDERS } from "@/store/constants/action-types"
import messages from "@/PreIngest/messages.json"
import CreateProcessingRecord from "@/PreIngest/ProcessingRecords/CreateProcessingRecord"

/**
 * initialData
 */
function initialData() {
  return {
    startDate: null,
    endDate: null,
    lwsDate: null,
    lweDate: null,
    startDateMenu: false,
    endDateMenu: false,
    lwsDateMenu: false,
    lweDateMenu: false,
    packageId: "",
    title: "",
    manifestId: "",
    pct: "",
    packageAssetName: "",
    statesList: [
      "Ingested",
      "Audit",
      "Publish",
      "Complete",
      "Error",
      "Processing"
    ],
    additionalSearchFields: [
      { id: 1, searchField: "Package Id", visible: false },
      { id: 2, searchField: "Manifest Id", visible: false },
      { id: 3, searchField: "Added Date", visible: false },
      { id: 4, searchField: "PCT", visible: false },
      { id: 5, searchField: "Origination Id", visible: false },
      { id: 6, searchField: "Package Asset Name", visible: false }
    ],
    additionalSearchFieldsSelected: [],
    providerName: "",
    originationId: "",
    preIngestTabs: ["SEARCH"],
    active: null,
    selectedStates: [],
    selectedProviders: [],
    loadingProviders: false
  }
}

export default {
  name: "ProcessingRecordsSearchFilter",
  components: {
    CreateProcessingRecord
  },
  data: function () {
    return initialData()
  },
  computed: {
    ...mapState(["providers"]),
    maxStartDate() {
      return this.endDate
    },
    minEndDate() {
      return this.startDate
    },
    lwMaxStartDate() {
      return this.lweDate
    },
    lwMinEndDate() {
      return this.lwsDate
    }
  },
  methods: {
    ...mapActions([GET_PROVIDERS]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
      this.retrieveProviders()
    },
    /**
     * activeTabClass
     */
    activeTabClass(tab) {
      if (tab === this.active) {
        return "active_tab"
      }
    },

    /**
     * getbyAddedDateString
     */
    getbyAddedDateString(startDate, endDate) {
      let unixStartDate = ""
      let unixEndDate = ""
      const dateFormat = "YYYY-MM-DD"
      if (this.$lodash.isNull(startDate) && this.$lodash.isNull(endDate)) {
        return ""
      }
      if (!this.$lodash.isNull(startDate)) {
        unixStartDate = moment(startDate, dateFormat).unix() * 1000
      }
      if (!this.$lodash.isNull(endDate)) {
        unixEndDate =
          moment(endDate, dateFormat)
            .endOf("day")
            .unix() * 1000
      }
      return unixStartDate + "~" + unixEndDate
    },

    getLWDateString(date, type) {
      let lwDateString = ""
      let localDateTime = moment(date)
      let format = "YYYY-MM-DDTHH:mm:ss"
      if (!this.$lodash.isNull(date)) {
        if (type === "lwe") {
          localDateTime
            .hours(23)
            .minutes(59)
            .seconds(59)
        }
        lwDateString = localDateTime.format(format)
      }

      return lwDateString
    },

    /**
     *  Retrieve selected providers list
     */
    getSelectedProviders() {
      let selectedProvidersArray = []
      for (let item in this.selectedProviders) {
        selectedProvidersArray.push(this.selectedProviders[item].title)
      }
      return selectedProvidersArray.join("|")
    },

    /**
     * Constructs search object and emits event to begin searching for assets
     */
    searchProcessingRecords() {
      let searchQueryObject = {
        byState: this.selectedStates.join("|"),
        byProvider: this.getSelectedProviders(),
        byTitlePrefix: this.title,
        byLicensingWindowStart: this.getLWDateString(this.lwsDate, "lws"),
        byLicensingWindowEnd: this.getLWDateString(this.lweDate, "lwe")
      }

      // Check if additional search fields were selected
      if (this.additionalSearchFieldsSelected.length > 0) {
        this.$lodash.forEach(this.additionalSearchFieldsSelected, fieldId => {
          switch (fieldId) {
            case 1:
              searchQueryObject["byPaid"] = this.packageId
              break
            case 2:
              searchQueryObject["byManifestId"] = this.manifestId
              break
            case 3:
              searchQueryObject["byAdded"] = this.getbyAddedDateString(
                this.startDate,
                this.endDate
              )
              break
            case 4:
              searchQueryObject["byPcts"] = this.pct
              break
            case 5:
              searchQueryObject["byOriginationId"] = this.originationId
              break
            case 6:
              searchQueryObject["byPackageAssetName"] = this.packageAssetName
              break
          }
        })
      }

      let filteredSearchObject = this.$lodash.reduce(
        searchQueryObject,
        (result, value, key) => {
          if (value !== null && value.length > 0) {
            result[key] = value
          }
          return result
        },
        {}
      )
      PreIngestService.$emit(
        "event:preIngestProcessingRecordSerach",
        filteredSearchObject
      )
    },

    /**
     * retrieveProviders - Retrieve all providers from API service
     * @return {Promise}  promise
     */
    retrieveProviders() {
      this.loadingProviders = true
      this.GET_PROVIDERS()
        .catch(() => {
          // TODO: handle error
        })
        .finally(() => {
          this.loadingProviders = false
        })
    },

    /**
     * createProcessingRecord - Open the modal for creating processing record
     */
    createProcessingRecord() {
      PreIngestService.$emit("event:createProcessingRecord")
    },

    /**
     *
     */
    isAdditionalSearchFieldVisible(field) {
      return field.visible
    },

    /**
     *
     */
    updateSelection(item) {
      const index = this.additionalSearchFieldsSelected.indexOf(item.id)
      if (index > -1) {
        this.additionalSearchFieldsSelected.splice(index, 1)
        let searchField = this.$lodash.find(this.additionalSearchFields, {
          id: item.id
        })
        searchField.visible = false
        // Clear out date when the field is removed
        switch (item.id) {
          case 1:
            this.packageId = ""
            break
          case 2:
            this.manifestId = ""
            break
          case 3:
            this.startDate = null
            this.endDate = null
            break
          case 4:
            this.pct = ""
            break
          case 5:
            this.originationId = ""
            break
          case 6:
            this.packageAssetName = ""
            break
        }
      } else {
        this.additionalSearchFieldsSelected.push(item.id)
        let searchField = this.$lodash.find(this.additionalSearchFields, {
          id: item.id
        })
        searchField.visible = true
      }
    }
  },
  created() {
    this.messages = messages
    //  Retrieve providers list
    this.retrieveProviders()
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/PreIngest/ProcessingRecords/ProcessingRecordsSearchFilter.vue